/**
 * CUSTOMER FIX Stage 7 — multi-tenant cart switcher (header cart tap).
 */

import { useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  rememberSalesPointForTenant,
  rememberSalesPointIdForTenant,
} from '../shared/session/lastKnownSalesPoint.js';
import { useOptionalOverlayBackDismiss } from '../shared/context/OverlayBackDismissContext.js';
import type { CartRegistryRow } from '../features/shop/cart/cartRegistry.js';
import { buildDefaultShopHref } from '../features/shop/cart/cartShopHref.js';
import { resolveLastKnownSalesPointHref } from '../features/shop/resolveShopHref.js';
import { HEADER_OPEN_CART_STATE } from './buildHeaderOpenCartNavigateArgs.js';
import { CustomerChromeTopPanel } from './CustomerChromeTopPanel.js';

export interface MultiCartSwitcherSheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly rows: readonly CartRegistryRow[];
}

function resolveRowShopHref(row: CartRegistryRow): string {
  const href = row.shopHref?.trim();
  if (href !== undefined && href.length > 0) {
    return href;
  }
  const lastKnown = resolveLastKnownSalesPointHref(row.tenantCode);
  if (lastKnown !== null) {
    return lastKnown;
  }
  return buildDefaultShopHref(row.tenantCode, row.kioskId);
}

export function MultiCartSwitcherSheet({
  open,
  onClose,
  rows,
}: MultiCartSwitcherSheetProps): JSX.Element {
  const { t } = useTranslation('customer');
  const navigate = useNavigate();
  const titleId = useId();
  const descriptionId = useId();
  const overlayBack = useOptionalOverlayBackDismiss();
  const registerOverlayDismiss = overlayBack?.registerOverlayDismiss;

  useEffect(() => {
    if (!open || registerOverlayDismiss === undefined) {
      return;
    }
    return registerOverlayDismiss(onClose);
  }, [open, onClose, registerOverlayDismiss]);

  const onSelectRow = (row: CartRegistryRow): void => {
    rememberSalesPointIdForTenant(row.tenantCode, row.kioskId);
    rememberSalesPointForTenant(row.tenantCode, row.kioskId, row.salesPointLabel);
    const href = resolveRowShopHref(row);
    onClose();
    navigate(href, { state: HEADER_OPEN_CART_STATE });
  };

  return (
    <CustomerChromeTopPanel
      open={open}
      onClose={onClose}
      title={t('shop.multiCart.switcherTitle')}
      titleId={titleId}
      describedBy={descriptionId}
      closeLabel={t('shop.cartSheet.close.aria')}
      testId="customer-multi-cart-switcher"
      contentAlign="start"
      className="bg-[var(--color-surface-elevated)]"
    >
      <p
        id={descriptionId}
        className="mb-3 text-sm text-[var(--color-on-surface-muted)]"
      >
        {t('shop.multiCart.switcherDescription')}
      </p>
      <ul className="flex flex-col gap-2" data-testid="customer-multi-cart-switcher-list">
        {rows.map((row) => (
          <li key={`${row.tenantCode}:${row.kioskId}`}>
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-3 text-left hover:bg-[var(--color-surface-elevated)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]"
              data-testid={`customer-multi-cart-row-${row.tenantCode}-${row.kioskId}`}
              onClick={() => {
                onSelectRow(row);
              }}
            >
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white"
                style={{ backgroundColor: row.tenantColor }}
                aria-hidden
              >
                {row.tenantInitial}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-[var(--color-on-surface)]">
                  {row.tenantName}
                </span>
                <span className="block truncate text-xs text-[var(--color-on-surface-muted)]">
                  {row.salesPointLabel}
                </span>
              </span>
              <span className="shrink-0 text-sm font-medium text-[var(--color-on-surface)]">
                {t('shop.cart.items', { count: row.totalQuantity })}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </CustomerChromeTopPanel>
  );
}
